import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Header from './Header';
import InterviewerSection from './InterviewerSection';
import QuestionSection from './QuestionSection';
import useCaseMasterStore from './CaseMasterStore';
import TourStartDialog from 'dialogs/TourStartDialog';
import { CasemasterAssessmentStates, QuestionStates, CaseMasterTourSteps } from './constant';
import { ProctoringContext, TourProviderContext, useLoading, useServices, useSnackbar } from 'contexts';
import { getCaseMasterProblemById, createCaseMasterAttempt, startCaseMasterAttempt, finishCaseMasterAttempt } from 'services';
import {
  getClientPrefix, Session, getCompletedCaseMasterAttempt, markCaseMasterAttemptDone
} from 'utils';
import ActionTimerDialog from 'dialogs/ActionTimerDialog';
import FullscreenPermissionDialog from 'dialogs/FullscreenPermissionDialog';
import AnalysisScreen from './AnalysisScreen';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#fff',
    overflow: 'hidden'
  },
  header: {
    height: '76px',
  },
  content: {
    display: 'flex',
    gap: theme.spacing(5),
    height: 'calc(100vh - 76px)',
    justifyContent: 'space-between',
    padding: theme.spacing(0,5),
    overflow: 'auto',
  },
  quesSection: {
    width: 'calc(100vw - 320px)',
    display: 'flex',
    borderLeft: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
  },
  fullscreenContainer: {
    position: 'relative',
    bottom: '165px',
    margin: '0 auto',
    backgroundColor: '#003E8C',
    color: '#FFFFFF',
  }
}));

const CaseMasterAssessment = () => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();
  const loading = useLoading();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, tagId } = location.state || {};
  const user = Session.userId;
  const cid = searchParams.get("cid");

  // This route creates a brand new attempt on every mount, so landing back on
  // it - browser back from the report, forward, a pasted URL - would spend
  // another attempt, the only one on a contest. A finished attempt leaves a
  // marker; reading it once at mount (not on every render) keeps finishing the
  // current attempt from bouncing this component mid-teardown.
  const completedAttemptId = useRef(getCompletedCaseMasterAttempt(cid)).current;

  const exitPath = `/${getClientPrefix(user)}${data === 'contest'
    ? '/home'
    : '/personalized-interview?type=case-master'}`;

  const [openTourDialog, setOpenTourDialog] = useState(false);
  const [openFinalSubmittionDialog, setOpenFinalSubmittionDialog] = useState(false);
  const [openFullscreenDialog, setOpenFullscreenDialog] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finished, setFinished] = useState(false);

  const { setSteps, play } = useContext(TourProviderContext);
  const assessmentState = useCaseMasterStore(state => state.assessmentState);
  const setAssessmentState = useCaseMasterStore(state => state.setAssessmentState);

  const [
    startAssessment, endTutorial, setCasemasterProblem, casemasterProblem,
    setAttempt, attempt, timeOut, questionState, reset,
    setTotalDuration, hasGivenResponse
  ] = useCaseMasterStore(state => [
    state.startAssessment, state.endTutorial, state.setCasemasterProblem, state.casemasterProblem,
    state.setAttempt, state.attempt, state.timeOut, state.questionState, state.reset,
    state.setTotalDuration, state.hasGivenResponse
  ]);

  useEffect(() => {
    reset();
  }, []); // Empty dependency array - only run once on mount

  const {
    endProctoring,
    getProctoringData,
    activateFullScreenProctoring,
    activateGeneralProctoring,
    enableFullscreenMode,
    fullScreenActive
  } = useContext(ProctoringContext);

  const fetchCasemasterProblem = useCallback(async () => {
    try {
      loading.show();

      if (!cid) {
        throw new Error("CaseMaster Problem ID is required");
      }

      const problemData = await getCaseMasterProblemById(cid);  

      const { topic, problem, title, difficulty, otherDetails } = problemData;

      setTotalDuration(1800);
      console.log('Set total duration to 1800 seconds');

      const casemasterProblemObj = {
        _id: cid,
        title: title || 'Case Study Challenge',
        topic: topic || '',
        problem: problem || '',
        description: `<div style="line-height: 1.6;">
          <h3 style="color: #1961BF; margin-bottom: 16px;">Topic: ${topic || 'Case Study'}</h3>
          <p style="margin-bottom: 12px;">You have <strong>30:00 mins</strong> to complete the case study</p>
          
          <div style="background-color: #F5F7FA; padding: 16px; border-radius: 8px; margin: 16px 0;">
            <h4 style="margin-top: 0; margin-bottom: 8px;">Case Introduction:</h4>
            <p style="margin: 0;">${problem || 'No problem description available.'}</p>
          </div>
        </div>`,
        difficulty: difficulty || 'Medium',
        duration: 30,
        entryPoint: data,
        otherDetails: otherDetails || {}
      };

      console.log('Setting casemaster problem:', casemasterProblemObj);
      setCasemasterProblem(casemasterProblemObj);

      // Create attempt after fetching the problem
      console.log('Creating casemaster attempt...');
      const attemptData = {
        user: Session.userId,
        casemasterProblem: cid,
        ...(data === 'contest' && tagId ? { casemasterContest: tagId } : {}),
      };

      const attemptResponse = await createCaseMasterAttempt(attemptData);
      console.log('Attempt created:', attemptResponse);

      // Every answer upload is keyed by this id. Without it the analyse API is
      // sent the string "undefined" and fails, so stop before the user talks
      // into a session that cannot save a word of it.
      if (!attemptResponse?._id) {
        throw new Error("Attempt was created without an id");
      }

      // Set the attempt in the store
      setAttempt(attemptResponse);

      console.log('Setting assessment state to STARTING');
      setAssessmentState(CasemasterAssessmentStates.STARTING);
    } catch (error) {
      console.error('Error fetching casemaster problem:', error);
      // A placeholder case and a fake `fallback-attempt-id` used to be
      // substituted here. That produced a session with no case text whose
      // answers the analyse API rejects - and, on a contest, spent the user's
      // only attempt. Fail loudly instead.
      snackbar.error("Uh Oh! Unable to start this case study. Please try again.");
      navigate(exitPath, { replace: true });
    } finally {
      loading.hide();
    }
  }, [
    cid, loading, setCasemasterProblem, setTotalDuration, setAttempt,
    setAssessmentState, snackbar, data, tagId, navigate, exitPath
  ]);

  const startAttempt = useCallback(async () => {
    // `Object.keys(attempt).length` used to pass an attempt that carried no
    // `_id`, which is the one field the answer uploads need.
    if (!attempt?._id) {
      console.error('No usable attempt to start:', attempt);
      snackbar.error("Unable to start this case study. Please try again.");
      navigate(exitPath, { replace: true });
      return;
    }

    try {
      loading.show();
      console.log('Starting assessment with existing attempt:', attempt);

      // Call the start attempt API with current timestamp
      const startData = {
        startedAt: new Date().toISOString()
      };

      await startCaseMasterAttempt(attempt._id, startData);
      console.log('Attempt started successfully at:', startData.startedAt);

      // Now start the assessment (timer and proctoring)
      setTimeout(() => startAssessment(), 1000);
    } catch (error) {
      console.error('Error starting attempt:', error);
      snackbar.error("Unable to start the assessment. Please try again.");
      navigate(exitPath, { replace: true });
    } finally {
      loading.hide();
    }
  }, [attempt, startAssessment, snackbar, loading, navigate, exitPath]);

  const handleFinishAssessment = useCallback(async () => {
    if (finishing || assessmentState !== CasemasterAssessmentStates.FINISHING) return;

    try {
      setOpenFinalSubmittionDialog(false);
      setFinishing(true);
      endProctoring();
      const proctorRecords = getProctoringData(true);

      await finishCaseMasterAttempt(attempt._id, {
        finishedAt: new Date().toISOString(), 
        proctorRecords
      });

      console.log('Assessment finished successfully');

      if (!hasGivenResponse) {
        console.log('No responses given during assessment');
        reset();
        snackbar.info("Assessment ended. No responses recorded.");
        navigate(exitPath, { replace: true });
        return;
      }

      // Only this path produces a report, so only this path may redirect a
      // re-entry to it instead of starting the case again.
      markCaseMasterAttemptDone(cid, attempt._id);

      setFinished(true);
    } catch (error) {
      console.error('Error finishing assessment:', error);
      snackbar.error("Uh Oh! Unable to finish the attempt");
      navigate(exitPath, { replace: true });
    }
  }, [finishing, assessmentState, endProctoring, getProctoringData, attempt, snackbar, navigate, hasGivenResponse, reset, cid, exitPath]);

  const handleSuccessFullscreenMode = () => {
    enableFullscreenMode();
    setOpenFullscreenDialog(false);
    if (assessmentState === CasemasterAssessmentStates.FULL_SCREEN_PERMISSION) {
      setAssessmentState(CasemasterAssessmentStates.SHOWING_TUTORIAL);
    }
  };

  useEffect(() => {
    if (completedAttemptId) return;

    console.log('Assessment state changed to:', assessmentState);
    switch (assessmentState) {
      case CasemasterAssessmentStates.SHOWING_TUTORIAL: {
        console.log('Showing tutorial...');
        const shown = JSON.parse(localStorage.getItem("case-master-interview-tour"));
        if (play || openTourDialog) break;
        if (shown && !openTourDialog) endTutorial();
        else {
          setSteps(CaseMasterTourSteps);
          setOpenTourDialog(true);
          localStorage.setItem("case-master-interview-tour", "true");
        }
        break;
      }
      case CasemasterAssessmentStates.FETCHING_CASEMASTER_PROBLEM: {
        console.log('Fetching casemaster problem...');
        fetchCasemasterProblem();
        break;
      }
      case CasemasterAssessmentStates.STARTING: {
        console.log('Starting attempt...');
        startAttempt();
        break;
      }
      case CasemasterAssessmentStates.RUNNING: {
        console.log('Assessment running...');
        activateFullScreenProctoring();
        activateGeneralProctoring();
        break;
      }
      case CasemasterAssessmentStates.FULL_SCREEN_PERMISSION: {
        console.log('Requesting fullscreen permission...');
        setOpenFullscreenDialog(true);
        break;
      }
      case CasemasterAssessmentStates.FINISHING: {
        console.log('Finishing assessment...');
        handleFinishAssessment();
        break;
      }
      default:
        console.log('Unknown assessment state:', assessmentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentState, play, completedAttemptId]);

  useEffect(() => {
    setOpenFinalSubmittionDialog(timeOut);
  }, [timeOut]);

  useEffect(() => {
    const listener = () => window.history.go(1);

    window.addEventListener('popstate', listener);
    console.log("Added popstate event");

    return () => {
      console.log("Removed popstate event");
      window.removeEventListener('popstate', listener);
      endProctoring();
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    const handleUnload = (event) => {
      if (window.location.pathname.includes("/case-master-assessment")) {
        sessionStorage.setItem('caseMasterAssessmentLeft', 'true');
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('caseMasterAssessmentLeft') === 'true') {
      sessionStorage.removeItem('caseMasterAssessmentLeft');
      navigate(-1, { replace: true });
    }
  }, [navigate, location.pathname]);

  const handleCloseTourDialog = useCallback((value) => {
    setOpenTourDialog(value);
  }, []);

  const handleCloseActionTimer = useCallback(() => {
    setAssessmentState(CasemasterAssessmentStates.FINISHING);
  }, [setAssessmentState]);

  useEffect(() => {
    if (!completedAttemptId) return;

    navigate(
      `/${getClientPrefix(user)}/performance/case-master/${completedAttemptId}`,
      { replace: true }
    );
  }, [completedAttemptId, navigate, user]);

  // Re-entered an attempt that is already done: render nothing while the
  // redirect above takes over.
  if (completedAttemptId) return null;

  return finishing ? <AnalysisScreen isFeedbackGenerated={finished} /> : (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Header />
      </Box>
      <Box className={classes.content}>
        <InterviewerSection />
        <Box className={classes.quesSection}>
          <QuestionSection />
        </Box>
      </Box>

      <TourStartDialog
        open={openTourDialog}
        onClose={handleCloseTourDialog}
        showCloseButton={false}
        title="Case Study Interview Walkthrough"
        description={
          <>Before proceeding to the case study interview, it's necessary to understand how it works. This walkthrough
            <strong> will not be repeated</strong>.
            Please read instructions carefully to avoid any confusion. Good luck!
          </>
        }
        image='https://assets.languify.in/gifs/interviewScreenGif.gif'
        buttonLabel="Let's get started"
      />
      {
        openFinalSubmittionDialog &&
        <ActionTimerDialog
          open={openFinalSubmittionDialog}
          onClose={handleCloseActionTimer}
          title={`It was great talking to you, ${Session.user.name}`}
          timerMessage='Auto-submitting {timeLeft} seconds'
        />
      }

      <FullscreenPermissionDialog
        open={
          openFullscreenDialog ||
          (!fullScreenActive && assessmentState === CasemasterAssessmentStates.RUNNING)
        }
        onSuccess={handleSuccessFullscreenMode}
      />
    </Box>
  );
};

export default CaseMasterAssessment;
